import React, { useState, useEffect, useCallback } from 'react';

interface SetupCelebrationProps {
  show: boolean;
  onDismiss: () => void;
}

export default function SetupCelebration({ show, onDismiss }: SetupCelebrationProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (show) {
      // Small delay before showing for smoother UX
      const timer = setTimeout(() => setIsVisible(true), 100);
      return () => clearTimeout(timer);
    }
  }, [show]);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsExiting(false);
      onDismiss();
    }, 300);
  }, [onDismiss]);

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (isVisible && !isExiting) {
      const timer = setTimeout(handleDismiss, 8000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isExiting, handleDismiss]);

  if (!show && !isVisible) return null;

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] transition-all duration-300 ease-out ${
        isVisible && !isExiting
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-8'
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl shadow-2xl border-2 border-emerald-400/50 bg-gradient-to-r from-emerald-900/95 via-cyan-900/95 to-emerald-900/95 backdrop-blur-xl">
        {/* Animated gradient border effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-cyan-400/20 to-emerald-500/20 animate-pulse" />
        
        {/* Sparkle effects */}
        <div className="absolute top-2 left-4 w-2 h-2 bg-yellow-300 rounded-full animate-ping opacity-75" />
        <div className="absolute top-4 right-8 w-1.5 h-1.5 bg-cyan-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-3 left-12 w-1.5 h-1.5 bg-emerald-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '1s' }} />
        
        <div className="relative px-6 py-5 flex items-center gap-5">
          {/* Trophy/Success Icon */}
          <div className="shrink-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30 animate-bounce" style={{ animationDuration: '2s' }}>
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-black text-white flex items-center gap-2">
              You're Raid Ready!
              <span className="text-xl">🎉</span>
            </h3>
            <p className="text-sm text-emerald-100/80 mt-0.5">
              Character created + availability set. Raid leaders can now find and assign you!
            </p>
          </div>

          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="shrink-0 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors text-white/60 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress bar for auto-dismiss */}
        {isVisible && !isExiting && (
          <div className="h-1 bg-black/20">
            <div 
              className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400"
              style={{
                animation: 'shrink 8s linear forwards',
              }}
            />
          </div>
        )}
      </div>

      {/* CSS for the progress bar animation */}
      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
